import { useState } from "react";
import * as XLSX from "xlsx";
import CourseTag from "../../components/CourseTag";
import Layout from "../../components/Layout";
import Title from "../../components/Title";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Column {
    id: number;
    name: string;
    value: string;
}

interface ModalState {
    courseValue: number;
    courseName: string;
}

// ─── Default columns matching the screenshot ─────────────────────────────────
const DEFAULT_COLUMNS: Column[] = [
    { id: 1, name: "Khoá học",      value: "" },
    { id: 2, name: "Môn",           value: "" },
    { id: 3, name: "Lớp",           value: "" },
    { id: 4, name: "Thời gian",     value: "" },
    { id: 5, name: "Tên giáo viên", value: "" },
    { id: 6, name: "Số điện thoại", value: "" },
    { id: 7, name: "Số buổi",       value: "" },
];

let _idCounter = 100;

// ─── Course name map ──────────────────────────────────────────────────────────
const COURSE_NAMES: Record<number, string> = {
    1: "Tiền Tiểu Học",
    2: "Cấp 1",
    3: "Cấp 2",
    4: "Cấp 3",
    5: "Tin học",
};

// ─── Excel export ─────────────────────────────────────────────────────────────
function exportToExcel(columns: Column[], courseName: string) {
    const headers = columns.map((c) => c.name);
    const values  = columns.map((c) => c.value);

    const ws = XLSX.utils.aoa_to_sheet([headers, values]);

    // Style header row (column widths)
    ws["!cols"] = headers.map(() => ({ wch: 20 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Danh sách");
    XLSX.writeFile(wb, `${courseName}_danh_sach.xlsx`);
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconPlus = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const IconTrash = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6M14 11v6M9 6V4h6v2" />
    </svg>
);
const IconDownload = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);
const IconEye = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const IconClose = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconBack = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

// ─── Preview Table ────────────────────────────────────────────────────────────
function PreviewTable({ columns }: { columns: Column[] }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.id}
                                className="px-4 py-3 text-left text-white font-semibold whitespace-nowrap border-r border-white/20 last:border-r-0"
                                style={{ background: "#1a7c3e" }}
                            >
                                {col.name || <span className="opacity-40 italic">Cột {col.id}</span>}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr className="bg-green-50">
                        {columns.map((col) => (
                            <td
                                key={col.id}
                                className="px-4 py-3 border-r border-t border-gray-200 last:border-r-0 whitespace-nowrap"
                                style={{ color: col.value ? "#1a2e1f" : "#aaa", fontStyle: col.value ? "normal" : "italic" }}
                            >
                                {col.value || "—"}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

// ─── Excel Export Modal ───────────────────────────────────────────────────────
function ExcelExportModal({
    modal,
    onClose,
}: {
    modal: ModalState | null;
    onClose: () => void;
}) {
    const [columns, setColumns]             = useState<Column[]>(DEFAULT_COLUMNS);
    const [step, setStep]                   = useState<"edit" | "preview">("edit");
    const [justExported, setJustExported]   = useState(false);

    if (!modal) return null;

    const addColumn = () =>
        setColumns((prev) => [...prev, { id: ++_idCounter, name: "", value: "" }]);

    const removeColumn = (id: number) =>
        setColumns((prev) => prev.filter((c) => c.id !== id));

    const updateColumn = (id: number, field: keyof Column, val: string) =>
        setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: val } : c)));

    const handleExport = () => {
        exportToExcel(columns, modal.courseName);
        setJustExported(true);
        setTimeout(() => setJustExported(false), 2500);
    };

    const handleClose = () => {
        setStep("edit");
        setColumns(DEFAULT_COLUMNS);
        setJustExported(false);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-5"
            style={{ background: "rgba(10,30,15,0.55)", backdropFilter: "blur(5px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div
                className="bg-white rounded-2xl flex flex-col overflow-hidden w-full"
                style={{
                    maxWidth: step === "preview" ? 880 : 640,
                    maxHeight: "90vh",
                    boxShadow: "0 32px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(26,124,62,0.1)",
                    transition: "max-width 0.3s ease",
                }}
            >
                {/* ── Header ── */}
                <div
                    className="flex items-center justify-between px-7 py-5 border-b border-gray-100"
                    style={{ background: "linear-gradient(135deg,#f8fffe,#f0faf4)" }}
                >
                    <div className="flex items-center gap-3">
                        {step === "preview" && (
                            <button
                                onClick={() => setStep("edit")}
                                className="w-8 h-8 rounded-lg flex items-center justify-center border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            >
                                <IconBack />
                            </button>
                        )}
                        <div>
                            <p className="text-xs font-bold tracking-widest uppercase text-green-700 mb-0.5">
                                Xuất Excel · {modal.courseName}
                            </p>
                            <h2 className="text-lg font-bold text-gray-900">
                                {step === "edit" ? "Cấu hình dữ liệu" : "Xem trước dữ liệu"}
                            </h2>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        <IconClose />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto flex-1 px-7 py-6">
                    {step === "edit" && (
                        <>
                            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
                                Nhập tên cột và giá trị tương ứng. Bạn có thể thêm hoặc xoá cột tuỳ ý.
                            </p>

                            {/* Column header labels */}
                            <div className="grid gap-2.5 mb-1 pl-1" style={{ gridTemplateColumns: "1fr 1fr 36px" }}>
                                <span className="text-xs font-bold tracking-widest uppercase text-gray-400">Tên cột</span>
                                <span className="text-xs font-bold tracking-widest uppercase text-gray-400">Giá trị</span>
                            </div>

                            {/* Column rows */}
                            <div className="flex flex-col gap-2">
                                {columns.map((col, idx) => (
                                    <div
                                        key={col.id}
                                        className="grid gap-2.5 items-center rounded-xl px-3 py-2 border border-gray-100"
                                        style={{
                                            gridTemplateColumns: "1fr 1fr 36px",
                                            background: idx % 2 === 0 ? "#fafafa" : "#fff",
                                        }}
                                    >
                                        <input
                                            value={col.name}
                                            onChange={(e) => updateColumn(col.id, "name", e.target.value)}
                                            placeholder={`Tên cột ${idx + 1}`}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-green-400 transition-colors bg-white"
                                        />
                                        <input
                                            value={col.value}
                                            onChange={(e) => updateColumn(col.id, "value", e.target.value)}
                                            placeholder="Nhập giá trị..."
                                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-green-400 transition-colors bg-white"
                                        />
                                        <button
                                            onClick={() => removeColumn(col.id)}
                                            disabled={columns.length <= 1}
                                            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                                            style={{
                                                background: columns.length <= 1 ? "#f5f5f5" : "#fff0f0",
                                                border: `1.5px solid ${columns.length <= 1 ? "#e5e5e5" : "#fecaca"}`,
                                                color: columns.length <= 1 ? "#ccc" : "#ef4444",
                                                cursor: columns.length <= 1 ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            <IconTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add column */}
                            <button
                                onClick={addColumn}
                                className="mt-3 w-full py-2.5 rounded-xl border-2 border-dashed border-green-200 text-green-700 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-green-50 hover:border-green-400 transition-all"
                            >
                                <IconPlus /> Thêm cột
                            </button>
                        </>
                    )}

                    {step === "preview" && (
                        <>
                            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                                Kiểm tra dữ liệu bên dưới trước khi xuất file. Ô trống hiển thị "—".
                            </p>
                            <PreviewTable columns={columns} />
                            <div className="mt-4 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800 flex items-center gap-2">
                                <span className="text-lg">📊</span>
                                <span>
                                    <strong>{columns.length}</strong> cột ·{" "}
                                    <strong>1</strong> hàng dữ liệu · Khóa học:{" "}
                                    <strong>{modal.courseName}</strong>
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
                    >
                        Hủy
                    </button>

                    {step === "edit" && (
                        <button
                            onClick={() => setStep("preview")}
                            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <IconEye /> Xem trước
                        </button>
                    )}

                    {step === "preview" && (
                        <>
                            <button
                                onClick={() => setStep("edit")}
                                className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
                            >
                                Chỉnh sửa
                            </button>
                            <button
                                onClick={handleExport}
                                className="px-6 py-2.5 rounded-xl text-white font-bold text-sm flex items-center gap-2 transition-all shadow-md min-w-[140px] justify-center"
                                style={{ background: justExported ? "#16a34a" : "#1a7c3e" }}
                            >
                                {justExported ? (
                                    <><span className="text-base">✓</span> Đã xuất!</>
                                ) : (
                                    <><IconDownload /> Xuất Excel</>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function Course() {
    const [modal, setModal] = useState<ModalState | null>(null);

    const handleExportExcel = (value: number) => {
        setModal({ courseValue: value, courseName: COURSE_NAMES[value] ?? `Khóa ${value}` });
    };

    return (
        <Layout>
            <section className="w-full h-fit py-[60px] px-[20px] lg:px-[50px] text-center bg-[#f9f9f9]">
                <Title title="Khóa Học" subTitle="Các khóa học đang được giảng dạy tại trung tâm" />
                <div className="flex flex-col lg:flex-row mt-[40px] lg:justify-start flex-wrap gap-y-[30px] lg:gap-x-[calc((100%-31%*3)/2)]">
                    <div className="lg:w-[31%]">
                        <CourseTag onExportExcel={(value) => handleExportExcel(value)} value={1} nameCourse="Tiền Tiểu Học" level="Cơ bản" titleFirst="Chuẩn bị kiến thức nền tảng" titleSecond="Làm quen với chữ cái và số" titleThird="Phát triển tư duy logic" />
                    </div>
                    <div className="lg:w-[31%]">
                        <CourseTag onExportExcel={(value) => handleExportExcel(value)} value={2} nameCourse="Cấp 1" level="Cơ bản & Nâng cao" titleFirst="Toán - Tiếng Việt - Tiếng Anh" titleSecond="Luyện chữ đẹp" titleThird="Phát triển kỹ năng toàn diện" />
                    </div>
                    <div className="lg:w-[31%]">
                        <CourseTag onExportExcel={(value) => handleExportExcel(value)} value={3} nameCourse="Cấp 2" level="Trung cấp" titleFirst="Toán - Ngữ Văn - Tiếng Anh" titleSecond="Khoa học tự nhiên" titleThird="Ôn thi vào lớp 10" />
                    </div>
                    <div className="lg:w-[31%]">
                        <CourseTag onExportExcel={(value) => handleExportExcel(value)} value={4} nameCourse="Cấp 3" level="Nâng cao" titleFirst="Toán - Lý - Hóa - Văn - Anh" titleSecond="Ôn thi Tốt nghiệp THPT" titleThird="Luyện thi Đại học" />
                    </div>
                    <div className="lg:w-[31%]">
                        <CourseTag onExportExcel={(value) => handleExportExcel(value)} value={5} nameCourse="Tin học" level="Từ cơ bản đến chuyên sâu" titleFirst="Tin học cơ bản đến thực chiến" titleSecond="Tin học ứng dụng văn phòng" titleThird="Ứng dụng thực tế chuyên sâu" />
                    </div>
                </div>
            </section>

            {/* Excel Export Modal */}
            <ExcelExportModal modal={modal} onClose={() => setModal(null)} />
        </Layout>
    );
}

export default Course;